
import {
  defineComponent, reactive, watch,
} from 'vue';
import Icone from '@/core/components/Icone.vue';
import RequisicaoModal from '@/core/components/Modal/RequisicaoModal.vue';
import { EStatusRetornoRequisicao, IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import { ETipoPermissao } from '@/models/Enumeradores/MeuSistema/Usuarios/ETipoPermissao';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useModalBase } from '@/core/composables/ModalBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import ServicoEngenhariaProduto from '@/servicos/Cadastros/PCP/ServicoEngenhariaProduto';
import {
  IEngenhariaEmpresa, IEngenhariaEtapa, IEngenhariaMateriaPrima, IEngenhariaProduto, IEngenhariaProdutoAcabado,
} from '@/models/Entidades/Cadastros/PCP/IEngenhariaProduto';
import SelecionarTipoProducao from '@/components/Cadastros/PCP/SelecionarTipoProducao.vue';
import SelecionarProduto from '@/components/Cadastros/Produtos/SelecionarProduto.vue';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import ServicoTipoProducao from '@/servicos/Cadastros/PCP/ServicoTipoProducao';
import SelecionarUnidadeProdutoMovimento from '@/components/Cadastros/Produtos/Unidades/SelecionarUnidadeProdutoMovimento.vue';
import EngenhariaEtapasProducao from '@/components/Cadastros/PCP/EngenhariaEtapasProducao.vue';
import { IUnidade } from '@/models/Entidades/Cadastros/Produtos/IUnidade';
import ServicoUnidade from '@/servicos/Cadastros/Produtos/ServicoUnidade';

export default defineComponent({
  name: 'EngenhariaProdutoModal',
  props: {
    visivel: {
      type: Boolean,
    },
    operacao: {
      type: Object as () => ITelaOperacao,
      required: true,
    },
  },
  components: {
    Icone,
    RequisicaoModal,
    SelecionarTipoProducao,
    SelecionarProduto,
    SelecionarUnidadeProdutoMovimento,
    CampoNumerico,
    EngenhariaEtapasProducao,
  },
  emits: ['sincronizarRegistro', 'update:operacao', 'update:visivel'],
  setup(props, { emit }) {
    const {
      telaBase, obterPermissoes, preencherEmpresasComEstrategiaPermissaoDados, defineEmpresasSelecionadasCadastroCompartilhado,
      preencherPermissoesDados, filtrarPermissaoDadosUsuarioMultiEmpresas, apresentarMensagemSucesso, verificaConceitoParaApresentarEmpresas,
      apresentarMensagemAlerta,
    } = useTelaBase();
    const {
      modalBase, apresentarRetornoRequisicao, apresentarBarraProgresso, ocultarBarraProgresso,
      defineNovaOperacao, defineTextoPadraoBotoes, sincronizarRegistro,
    } = useModalBase(props, emit);
    const servicoEngenhariaProduto = new ServicoEngenhariaProduto();
    const servicoTipoProducao = new ServicoTipoProducao();
    const servicoUnidade = new ServicoUnidade();
    telaBase.identificadorRecurso = 'CADASTRO_ETAPA_PRODUCAO';
    telaBase.identificadorPermissao = 'PER_CADASTRO_ETAPA_PRODUCAO';
    servicoTipoProducao.requisicaoSistema();
    servicoUnidade.requisicaoSistema();

    const state = reactive({
      engenharia: {} as IEngenhariaProduto,
      unidade: {} as IUnidade,
      editavel: true,
      descricao: '',
    });

    function objetoInicial() {
      state.engenharia = {} as IEngenhariaProduto;
      state.engenharia.empresas = [] as IEngenhariaEmpresa[];
      state.engenharia.etapasProducao = [] as IEngenhariaEtapa[];
      state.engenharia.cips = [];
      state.engenharia.ativo = true;
      telaBase.empresasSelecionadas = [];
      state.editavel = true;
      state.descricao = '';
    }

    function preparaPersistenciaEmpresas() {
      const engenhariaEmpresas : IEngenhariaEmpresa[] = [];
      if (telaBase.empresasSelecionadas.length > 0) {
        telaBase.empresasSelecionadas.forEach((codigoEmpresa) => {
          const empresaExistente = state.engenharia.empresas.find((c) => c.codigoEmpresa === codigoEmpresa);
          if (empresaExistente !== undefined) {
            engenhariaEmpresas.push(empresaExistente);
          } else {
            const engenhariaEmpresa: IEngenhariaEmpresa = { codigo: 0, codigoEngenharia: state.engenharia.codigo, codigoEmpresa };
            engenhariaEmpresas.push(engenhariaEmpresa);
          }
        });
      }
      state.engenharia.empresas = engenhariaEmpresas;
    }

    async function salvar(salvarNovo: boolean) {
      let retorno: IRetornoRequisicao = { codigoRegistro: 0, status: 0, mensagem: '' };

      apresentarBarraProgresso();
      preparaPersistenciaEmpresas();
      if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
        retorno = await servicoEngenhariaProduto.incluir(state.engenharia);
      } else if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
        retorno = await servicoEngenhariaProduto.alterar(state.engenharia);
      }

      ocultarBarraProgresso();

      if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
          sincronizarRegistro(EPermissaoDados.Incluir, retorno.codigoRegistro);
        } else {
          sincronizarRegistro(EPermissaoDados.Alterar, props.operacao.codigoRegistro);
        }
        apresentarMensagemSucesso(retorno.mensagem);
        if (salvarNovo) {
          objetoInicial();
          telaBase.empresasSelecionadas = [props.operacao.empresaSelecionada];
          defineNovaOperacao(props.operacao);
        } else {
          modalBase.computedVisivel = false;
        }
      } else {
        apresentarRetornoRequisicao(retorno);
      }
    }

    async function PreencherEtapaProducao() {
      state.engenharia.etapasProducao = [] as IEngenhariaEtapa[];
      const tipoProducao = await servicoTipoProducao.obter(state.engenharia.codigoTipoProducao, telaBase.empresasSelecionadas[0]);
      if (tipoProducao.etapas.length === 0) {
        apresentarMensagemAlerta('Nenhuma etapa definida para esse tipo de produção, não é possível adicionar na engenharia');
        state.engenharia.codigoTipoProducao = 0;
        return;
      }
      tipoProducao.etapas.forEach((e) => {
        const etapaProducao : IEngenhariaEtapa = {} as IEngenhariaEtapa;
        etapaProducao.codigoEtapaProducao = e.codigoEtapa;
        etapaProducao.descricao = e.descricao;
        etapaProducao.ordem = e.ordem;
        etapaProducao.materiasPrimas = [] as IEngenhariaMateriaPrima[];
        etapaProducao.produtosAcabados = [] as IEngenhariaProdutoAcabado[];
        state.engenharia.etapasProducao.push(etapaProducao);
      });
    }

    async function preencherProdutoAcabado() {
      const index = state.engenharia.etapasProducao.length - 1;
      const indexPA = state.engenharia.etapasProducao[index].produtosAcabados.findIndex((p) => p.codigoProduto === state.engenharia.codigoProduto);
      if (indexPA > -1) {
        state.engenharia.etapasProducao[index].produtosAcabados[indexPA].quantidade = state.engenharia.quantidadeFormula;
      } else {
        state.engenharia.etapasProducao[index].produtosAcabados = [] as IEngenhariaProdutoAcabado[];
        const produtoAcabado = {} as IEngenhariaProdutoAcabado;
        produtoAcabado.codigoEtapa = state.engenharia.etapasProducao[index].codigoEtapaProducao;
        produtoAcabado.codigoProduto = state.engenharia.codigoProduto;
        produtoAcabado.codigoUnidade = state.engenharia.codigoUnidade;
        produtoAcabado.quantidade = state.engenharia.quantidadeFormula;
        produtoAcabado.descricao = state.descricao;
        produtoAcabado.siglaUnidade = state.unidade.sigla;
        produtoAcabado.ordem = 1;
        state.engenharia.etapasProducao[index].produtosAcabados.push(produtoAcabado);
      }
    }

    watch(async () => modalBase.computedVisivel, async () => {
      telaBase.carregando = true;
      objetoInicial();
      if (modalBase.computedVisivel) {
        if (props.operacao.listaPermissoesDados.length > 0) {
          await preencherPermissoesDados(props.operacao.listaPermissoesDados);
        } else {
          await obterPermissoes(ETipoPermissao.Dados);
        }
        await preencherEmpresasComEstrategiaPermissaoDados(props.operacao.tipoPermissaoDados, true);

        defineTextoPadraoBotoes(props.operacao.tipoPermissaoDados);
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
          state.engenharia = await servicoEngenhariaProduto.obter(props.operacao.codigoRegistro, props.operacao.empresaSelecionada);
          if (!UtilitarioGeral.validaLista(state.engenharia.cips)) {
            state.engenharia.cips = [];
          }
          state.unidade = await servicoUnidade.obter(state.engenharia.codigoUnidade, props.operacao.empresaSelecionada);
          if (UtilitarioGeral.validaLista(state.engenharia.empresas)) {
            state.engenharia.empresas.forEach((e) => {
              telaBase.empresasSelecionadas.push(e.codigoEmpresa);
            });
          }
          state.engenharia.etapasProducao.forEach((etapa) => {
            etapa.materiasPrimas.forEach((mp) => {
              if (mp.materiasPrimasPI === null || mp.materiasPrimasPI === undefined) {
                // eslint-disable-next-line no-param-reassign
                mp.materiasPrimasPI = [] as IEngenhariaMateriaPrima[];
              }
            });
          });
        } else {
          await defineEmpresasSelecionadasCadastroCompartilhado(props.operacao.empresaSelecionada);
        }

        telaBase.permissaoDados = await filtrarPermissaoDadosUsuarioMultiEmpresas(telaBase.empresasSelecionadas);
        verificaConceitoParaApresentarEmpresas();
      }
      telaBase.carregando = false;
    });
    watch(() => state.engenharia.codigoUnidade, async () => {
      if (state.engenharia.codigoUnidade === undefined || state.engenharia.codigoUnidade === 0) {
        return;
      }
      state.unidade = await servicoUnidade.obter(state.engenharia.codigoUnidade, props.operacao.empresaSelecionada);
      state.engenharia.codigoUnidade = state.unidade.codigo;
    });

    return {
      telaBase,
      props,
      modalBase,
      state,
      salvar,
      objetoInicial,
      EPermissaoDados,
      PreencherEtapaProducao,
      preencherProdutoAcabado,
    };
  },
});
